import React from 'react';
import { useTranslation } from 'react-i18next';

const LAMP_CATEGORIES = ['action_required', 'follow_up', 'on_track'];

/**
 * Status header for the project detail page: a "Key Summary" label, a
 * three-lamp traffic light, the project performance sentence, an optional
 * list of warning cards, up to 3 supporting bullets, and the project's start/end
 * dates as light metadata. Always renders (with a "-" placeholder when
 * there's no data yet), matching how other empty fields on this page behave.
 *
 * The traffic light is shown only for on_track, follow_up, and
 * action_required. It stays hidden while the category is missing.
 *
 * Only data-backed bullets and warnings should be passed in — this component
 * never fabricates reasoning text.
 *
 * @param {Object} props
 * @param {string|null} props.color - Project performance color, or null for no status
 * @param {string|null} [props.category] - Performance category that lights one lamp
 * @param {string} [props.summary] - Project performance sentence, shown only when provided
 * @param {Array<{ text: string, type: string, category?: string }>} [props.warnings] - One card per warning.
 *   `type` (overdue, profitability, uninvoiced) selects the card title.
 *   `action_required`, `follow_up`, and `on_track` use that traffic-light lamp color.
 * @param {Array<{ text: string, accent?: boolean }>} [props.bullets] - Up to 3 later Key Summary reasons;
 *   `accent: true` gives that bullet its own colored marker instead of a plain one
 * @param {string} [props.startDate] - Formatted start date
 * @param {string} [props.endDate] - Formatted end date
 * @returns {JSX.Element}
 */
const ProjectStatusHeader = ({ color, category, summary, warnings = [], bullets = [], startDate, endDate }) => {
  const { t } = useTranslation();
  const visibleBullets = bullets.slice(0, 3);
  const showLamps = LAMP_CATEGORIES.includes(category);

  return (
    <div
      className="pd-status-header"
      style={{ '--pd-status-color': color || 'var(--color-text-tertiary)' }}
    >
      <div className="pd-status-header-top">
        <div className="pd-label">{t('projects.details.sectionKeySummary')}</div>
        <div className="pd-status-dates">
          {t('projects.details.startDate')}: {startDate || '-'} · {t('projects.details.endDate')}: {endDate || '-'}
        </div>
      </div>
      <div className="pd-status-summary-row">
        {showLamps && (
          <span className="pd-status-light" aria-hidden="true">
            {LAMP_CATEGORIES.map((lamp) => (
              <span
                key={lamp}
                className={`pd-status-lamp is-${lamp}${lamp === category ? ' is-on' : ''}`}
              />
            ))}
          </span>
        )}
        <span className="pd-status-summary">{summary || '-'}</span>
      </div>
      {warnings.length > 0 && (
        <ul className="pd-warning-cards">
          {warnings.map((warning, index) => (
            <li
              key={index}
              className={`pd-warning-card${
                LAMP_CATEGORIES.includes(warning.category) ? ` is-${warning.category}` : ''
              }`}
            >
              <svg
                className="pd-warning-icon"
                viewBox="0 0 24 24"
                width="24"
                height="24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              <div className="pd-warning-body">
                <div className="pd-label">{t(`projects.details.warningTitle.${warning.type}`)}</div>
                <div className="pd-warning-text">{warning.text}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
      {visibleBullets.length > 0 && (
        <ul className="pd-status-bullets">
          {visibleBullets.map((bullet, index) => (
            <li key={index} className="pd-status-bullet">
              <span
                className={`pd-status-bullet-marker${bullet.accent ? ' pd-status-bullet-marker-accent' : ''}`}
                aria-hidden="true"
              />
              <span>{bullet.text}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ProjectStatusHeader;
